import { useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { isVideoUrl } from "@/lib/media";

/**
 * MediaLightbox — fullscreen viewer with prev/next navigation and keyboard support.
 *
 * Props:
 *  - items: Array<string | { url: string, title?: string }>
 *  - index: number (currently displayed)
 *  - onClose: () => void
 *  - onChange: (newIndex) => void
 */
export default function MediaLightbox({ items, index, onClose, onChange }) {
  const normalized = (items || []).map((it) =>
    typeof it === "string" ? { url: it } : it
  );
  const total = normalized.length;
  const current = normalized[index];

  const goPrev = useCallback(() => {
    if (total < 2) return;
    onChange((index - 1 + total) % total);
  }, [index, total, onChange]);

  const goNext = useCallback(() => {
    if (total < 2) return;
    onChange((index + 1) % total);
  }, [index, total, onChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  if (!current) return null;
  const url = current.url;
  const isVideo = isVideoUrl(url);

  return (
    <div className="lightbox-backdrop" onClick={onClose} role="dialog" data-testid="lightbox">
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center text-white bg-black/40 hover:bg-white hover:text-black transition border border-white/30"
        data-testid="lightbox-close"
        aria-label="Fermer"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      {total > 1 && (
        <div className="absolute top-7 left-5 text-white text-xs tracking-widest uppercase" data-testid="lightbox-counter">
          {index + 1} / {total}
        </div>
      )}

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-3 lg:left-8 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white bg-black/40 hover:bg-white hover:text-black transition border border-white/30 z-10"
          data-testid="lightbox-prev"
          aria-label="Précédent"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
      )}

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-3 lg:right-8 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white bg-black/40 hover:bg-white hover:text-black transition border border-white/30 z-10"
          data-testid="lightbox-next"
          aria-label="Suivant"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      )}

      {/* Media */}
      <div onClick={(e) => e.stopPropagation()} className="max-w-[92vw] max-h-[88vh] flex items-center justify-center">
        {isVideo ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-w-[92vw] max-h-[88vh] object-contain bg-black"
            data-testid="lightbox-video"
          />
        ) : (
          <img
            src={url}
            alt={current.title || ""}
            className="max-w-[92vw] max-h-[88vh] object-contain"
            data-testid="lightbox-image"
          />
        )}
      </div>

      {current.title && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-white font-serif italic px-6">
          {current.title}
        </div>
      )}
    </div>
  );
}
